import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import ContentBaseLayout from '../shared/ContentBaseLayout';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import Pagination from '../../../shared/common/Pagination';
import ProductList from '../../../shared/common/ProductList';
import LibxchangeProductElement from '../shared/LibxchangeProductElement';
import { LibxGenresSidebar } from './LibxGenresSidebar';
import { useLocalContentGenres } from './api/useLocalContentGenres';
import { ProductSorting } from '../../../../models/Product';
import { useParams } from 'react-router';
import { useLibxProducts } from './api/useLibxProducts';
import BreadCrumbNav from '../../../shared/navigation/BreadcrumbNav';
import { FilterBar } from '../../../shared/common/FilterBar';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchLibxGenreProductsAggregationsQuickSearch } from '../../../api/marketplace/fetchLibxGenreProductsAggregationsQuickSearch';
import { useAddLibxProductToCollection } from '../productdetail/api/useAddLibxProductToCollection';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchLibxGenreAggregations } from '../../../api/marketplace/fetchLibxGenreAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../../shared/utils/togglz';
import { URL_LIBX_ROUTE } from '../constants';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';
import { DISPLAY_TYPE_MINIMAL, DISPLAY_TYPE_TILE } from '../../../shared/common/displayType/types';
import { useDisplayTypeSearchParam } from '../../../shared/utils/useDisplayTypeSearchParam';
import { createGenreBreadcrumbItems } from './createGenreBreadcrumbItems';
import { BreadcrumbsState } from '../../../shared/breadcrumbs/types';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';
import { useAuthentication } from '../../../app-store/authentication/useAuthentication';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../../shared/filter/useProductFilters';
import { useTranslation } from 'react-i18next';

const StResponsiveFilterBar = styled(ResponsiveFilterBar)`
    margin-bottom: 100px;
`;

const StFilterBar = styled(FilterBar)`
    padding: 0 0 25px 0;
`;

const StHeader = styled.div`
    height: 64px;
`;

type GenreParams = { genre?: string; subgenre?: string };

const getSelectedGenre = ({ genre, subgenre }: GenreParams) =>
    genre ? decodeURIComponent(genre) + (subgenre ? `->${decodeURIComponent(subgenre)}` : '') : undefined;

export const LibxGenresContainer: FC = () => {
    const { t } = useTranslation();
    const authentication = useAuthentication();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const { genre, subgenre, shopLanguage } = useParams<'genre' | 'subgenre' | 'shopLanguage'>();
    const libxRoute = isMultiLanguageShopEnabled ? `shop/${shopLanguage}/${URL_LIBX_ROUTE}` : URL_LIBX_ROUTE;

    const {
        isLoading: isGenresLoading,
        defaultGenre,
        standardGenres,
        localContentGenres
    } = useLocalContentGenres(shopLanguage);
    const { mutate: addToCollectionMutation, isLoading: isAddToCollectionLoading } = useAddLibxProductToCollection();

    const { displayFilter } = useDisplayFilterSearchParam();

    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD,
        defaultPageSize: 54
    });

    const { productFilter } = useProductFilters();
    const selectedGenre = getSelectedGenre({ genre, subgenre }) ?? defaultGenre;
    const { value: ageCategory } = useAgeCategorySearchParam();
    const { display } = useDisplayTypeSearchParam(DISPLAY_TYPE_MINIMAL);

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'libXGenrePage',
            selectedGenre,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups,
            shopLanguage
        ],
        [ageCategory, selectedGenre, shopLanguage]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchLibxGenreAggregations({ ...options, ageCategory, genre: selectedGenre!, shopLanguage }),
        [ageCategory, selectedGenre, shopLanguage]
    );
    const fetchAggregationsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchLibxGenreProductsAggregationsQuickSearch({
                ...options,
                genre: selectedGenre!,
                ageCategory,
                shopLanguage
            }),
        [ageCategory, selectedGenre, shopLanguage]
    );
    //endregion

    const { isLoading: isProductsLoading, data: libxProducts } = useLibxProducts({
        ageCategory,
        genre: selectedGenre,
        filterMap: productFilter,
        pagination: { sortBy, page, pageSize },
        enabled: !isGenresLoading,
        shopLanguage
    });
    const totalProducts = libxProducts?.totalHits ?? 0;
    const products = libxProducts?.hits ?? [];

    const buckets = createGenreBreadcrumbItems([...standardGenres, ...localContentGenres]);

    const breadcrumbs: BreadcrumbsState = {
        key: 'by-genre',
        label: 'By Genre',
        buckets,
        activeBucket: selectedGenre
    };

    const breadcrumbNavLinkParams = `?${new URLSearchParams({
        ageCategory,
        display,
        ...(displayFilter ? { displayFilter: '' } : {})
    }).toString()}`;

    const loading = isProductsLoading || isGenresLoading || isAddToCollectionLoading;

    return (
        <ContentBaseLayout
            documentTitle="Genres"
            topComponent={
                <>
                    <StHeader>
                        <BreadCrumbNav
                            breadcrumbs={breadcrumbs}
                            fixedLabel={t('components.genreSidebar.genres')}
                            path={libxRoute}
                            customGenreBase="genres"
                            floatingClass=""
                            queryParamString={breadcrumbNavLinkParams}
                        />
                    </StHeader>
                    <StFilterBar
                        loading={loading}
                        sortBy={sortBy}
                        totalProducts={totalProducts}
                        hideSelectButtons={true}
                        pageSize={pageSize}
                        hideGroupBy={true}
                        displayType={display}
                        excludeDisplayTypes={[DISPLAY_TYPE_TILE]}
                        excludeSorting={[
                            'narratorAZ',
                            'stock',
                            'reserve',
                            'durationLongToShort',
                            'durationShortToLong',
                            'priceHighToLow',
                            'priceLowToHigh'
                        ]}
                    />
                </>
            }
            sidebarComponent={
                selectedGenre ? (
                    displayFilter ? (
                        <StResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearch}
                        />
                    ) : (
                        <LibxGenresSidebar
                            localContentGenres={localContentGenres}
                            standardGenres={standardGenres}
                            selectedGenre={selectedGenre}
                            shopLanguage={shopLanguage}
                        />
                    )
                ) : null
            }
            contentComponent={
                (totalProducts === 0 && !loading) || (!selectedGenre && !loading) ? (
                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                ) : (
                    <div className="ca-main-inner">
                        <ProductList products={products} displayType={display} onAddPrice={async () => {}}>
                            {({ key, isListView, ...value }) => (
                                <LibxchangeProductElement
                                    {...value}
                                    key={key}
                                    displayType={display}
                                    onAddToCollection={addToCollectionMutation}
                                    shopLanguage={shopLanguage}
                                />
                            )}
                        </ProductList>
                        <Pagination totalItems={totalProducts} pageSize={pageSize} />
                    </div>
                )
            }
            loading={loading}
        />
    );
};
